// src/components/admin/ProductWarehouseManagement.tsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Chip,
  Drawer,
  useMediaQuery,
  useTheme,
  Pagination,
  FormHelperText,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Search,
  Refresh,
  Close,
  Store,
  Inventory,
  Inventory2,
  TrendingUp,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

// Import API hooks
import {
  useListProductWarehousesQuery,
  useAddProductWarehouseMutation,
  useUpdateProductWarehouseMutation,
  useDeleteProductWarehouseMutation,
  ProductWarehouse,
  AddProductWarehouseRequest,
  UpdateProductWarehouseRequest,
} from '../../features/admin/productWarehouseApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

import {
  useListWarehousesQuery,
  Warehouse,
} from '../../features/admin/warehouseApi.ts';

interface FormErrors {
  [key: string]: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ProductWarehouseManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get user role and vendor ID from Redux store
  const { role, vendorId, userName } = useSelector((state: any) => state.auth);
  const isVendor = role === 'vendor';

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [selectedProductFilter, setSelectedProductFilter] = useState<number>(0);
  const [selectedWarehouseFilter, setSelectedWarehouseFilter] = useState<number>(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductWarehouse | null>(null);
  const [formData, setFormData] = useState({
    product_id: 0,
    warehouse_id: 0,
    available_quantity: 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const itemsPerPage = 10;

  // API hooks
  const {
    data: productWarehousesResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListProductWarehousesQuery({
    page,
    limit: itemsPerPage,
    product_id: selectedProductFilter || undefined,
    warehouse_id: selectedWarehouseFilter || undefined,
    vendor_id: isVendor ? vendorId : undefined,
  });

  const {
    data: productsResponse,
    isLoading: isLoadingProducts
  } = useListProductsQuery({
    page: 1,
    limit: 1000,
    vendor_id: isVendor ? vendorId : undefined,
  });

  const {
    data: warehousesResponse,
    isLoading: isLoadingWarehouses,
    error: warehousesError
  } = useListWarehousesQuery();

  const [addProductWarehouse] = useAddProductWarehouseMutation();
  const [updateProductWarehouse] = useUpdateProductWarehouseMutation();
  const [deleteProductWarehouse, { isLoading: isDeleting }] = useDeleteProductWarehouseMutation();

  // Handle API response structure - Extract warehouses correctly
  const productWarehouses = productWarehousesResponse?.result || [];
  const products = productsResponse?.result || productsResponse?.data || [];
  
  // Try multiple possible response structures for warehouses
  let warehouses: Warehouse[] = [];
  if (warehousesResponse) {
    if (Array.isArray(warehousesResponse.warehouses)) {
      warehouses = warehousesResponse.warehouses;
    } else if (Array.isArray(warehousesResponse.result)) {
      warehouses = warehousesResponse.result;
    } else if (Array.isArray(warehousesResponse)) {
      warehouses = warehousesResponse;
    }
  }
  
  const totalRecords = productWarehousesResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  // Shopify-style color scheme
  const shopifyColors = {
    primary: '#008060',
    primaryHover: '#006e52',
    surface: '#f6f6f7',
    border: '#e1e3e5',
    textPrimary: '#202223',
    textSecondary: '#6d7175',
    success: '#008060',
    warning: '#ffc453',
    error: '#d72c0d',
  };

  // Helper function to get product image URL
  const getProductImageUrl = (item: ProductWarehouse): string | undefined => {
    if (!item.product_main_image) return undefined;
    
    // Remove any leading/trailing spaces and check if it's a valid URL
    const imagePath = item.product_main_image.trim();
    
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Handle different image path formats
    if (imagePath.startsWith('/')) {
      return `https://fftestservice.sarsetech.com${imagePath}`;
    }
    
    if (imagePath.startsWith('images/')) {
      return `https://fftestservice.sarsetech.com/${imagePath}`;
    }
    
    // Default case
    return `https://fftestservice.sarsetech.com/images/${imagePath}`;
  };

  // Helper function to get product initial for fallback
  const getProductInitial = (item: ProductWarehouse): string => {
    if (item.product_name) {
      return item.product_name.charAt(0).toUpperCase();
    }
    if (item.product_short_name) {
      return item.product_short_name.charAt(0).toUpperCase();
    }
    return 'P';
  };

  // Helper function to check if image loads successfully
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
  };

  // Get product details including images from products API
  const getProductWithImage = (productWarehouse: ProductWarehouse) => {
    if (!products || products.length === 0) return productWarehouse;
    
    const product = products.find(p => 
      (p.product_id || p.id) === productWarehouse.product_id
    );
    
    if (product) {
      return {
        ...productWarehouse,
        product_main_image: product.product_main_image || productWarehouse.product_main_image,
        product_name: product.prod_name || productWarehouse.product_name,
        product_short_name: product.prod_short_name || productWarehouse.product_short_name,
        product_price: product.product_price || productWarehouse.product_price,
        currency: product.currency || productWarehouse.currency,
      };
    }
    
    return productWarehouse;
  };

  // Debug product images
  useEffect(() => {
    if (productWarehouses.length > 0) {
      console.log('=== PRODUCT IMAGE DEBUG ===');
      productWarehouses.forEach((item, index) => {
        const enhancedItem = getProductWithImage(item);
        console.log(`Product ${index + 1}:`, {
          name: enhancedItem.product_name,
          short_name: enhancedItem.product_short_name,
          image_path: enhancedItem.product_main_image,
          full_url: getProductImageUrl(enhancedItem),
          has_image: !!enhancedItem.product_main_image,
          from_products_api: !!products.find(p => (p.product_id || p.id) === item.product_id)
        });
      });
    }
  }, [productWarehouses, products]);

  // Debug: Log warehouse data
  useEffect(() => {
    console.log('=== Warehouse Debug Info ===');
    console.log('Raw warehousesResponse:', JSON.stringify(warehousesResponse, null, 2));
    console.log('Extracted warehouses array:', warehouses);
    console.log('Warehouses length:', warehouses.length);
    console.log('isLoadingWarehouses:', isLoadingWarehouses);
    console.log('Active warehouses:', warehouses.filter((w: Warehouse) => w.status === 1));
    if (warehousesError) {
      console.error('Warehouses Error:', warehousesError);
    }
  }, [warehousesResponse, isLoadingWarehouses, warehousesError]);

  const isEditing = Boolean(editingItem);

  // Auto-refresh when search or filters change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchStr, selectedProductFilter, selectedWarehouseFilter, refetch]);

  // Handlers
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchStr(value);
    setPage(1);
  };

  const handleProductFilterChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    setSelectedProductFilter(value);
    setPage(1);
  };

  const handleWarehouseFilterChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    setSelectedWarehouseFilter(value);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      product_id: 0,
      warehouse_id: 0,
      available_quantity: 0,
    });
    setFormErrors({});
    setEditingItem(null);
  };

  const handleAddNew = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleEdit = (item: ProductWarehouse) => {
    // Check if vendor is trying to edit someone else's product
    if (isVendor && item.product_id) {
      const product = products.find(p => (p.product_id || p.id) === item.product_id);
      if (product && product.vendor_id !== vendorId) {
        setSnackbar({
          open: true,
          message: 'You can only edit your own product warehouses',
          severity: 'error'
        });
        return;
      }
    }

    setFormData({
      product_id: item.product_id,
      warehouse_id: item.warehouse_id,
      available_quantity: item.available_quantity,
    });
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'available_quantity' ? Number(value) : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // CRITICAL FIX: Separate handlers for product and warehouse
  const handleProductChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    console.log('=== PRODUCT CHANGE ===');
    console.log('Selected product_id:', value);
    
    setFormData(prev => ({
      ...prev,
      product_id: value
    }));

    if (formErrors.product_id) {
      setFormErrors(prev => ({ ...prev, product_id: '' }));
    }
  };

  const handleWarehouseChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    console.log('=== WAREHOUSE CHANGE ===');
    console.log('Selected warehouse_id:', value);
    
    setFormData(prev => ({
      ...prev,
      warehouse_id: value
    }));

    if (formErrors.warehouse_id) {
      setFormErrors(prev => ({ ...prev, warehouse_id: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.product_id || formData.product_id === 0) {
      newErrors.product_id = 'Product is required';
    }
    if (!formData.warehouse_id || formData.warehouse_id === 0) {
      newErrors.warehouse_id = 'Warehouse is required';
    }
    if (formData.available_quantity < 0) {
      newErrors.available_quantity = 'Quantity cannot be negative';
    }

    // Debug log to see what's happening
    console.log('=== FORM VALIDATION ===');
    console.log('Product ID:', formData.product_id);
    console.log('Warehouse ID:', formData.warehouse_id);
    console.log('Quantity:', formData.available_quantity);
    console.log('Errors:', newErrors);

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('=== FORM SUBMIT ===');
    console.log('Form Data:', formData);

    if (!validateForm()) {
      console.log('Validation failed:', formErrors);
      return;
    }

    setIsFormSubmitting(true);

    try {
      if (isEditing && editingItem) {
        const requestData: UpdateProductWarehouseRequest = {
          id: editingItem.id,
          product_id: formData.product_id,
          warehouse_id: formData.warehouse_id,
          available_quantity: formData.available_quantity,
        };

        console.log('Update request:', requestData);
        const result = await updateProductWarehouse(requestData).unwrap();

        setSnackbar({
          open: true,
          message: result.message || 'Product warehouse updated successfully!',
          severity: 'success'
        });
      } else {
        const requestData: AddProductWarehouseRequest = {
          product_id: formData.product_id,
          warehouse_id: formData.warehouse_id,
          available_quantity: formData.available_quantity,
        };

        console.log('Add request:', requestData);
        const result = await addProductWarehouse(requestData).unwrap();

        setSnackbar({
          open: true,
          message: result.message || 'Product warehouse added successfully!',
          severity: 'success'
        });
      }

      handleFormClose();
      refetch();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} product warehouse. Please try again.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this product warehouse mapping?')) {
      try {
        const result = await deleteProductWarehouse({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result.message || 'Product warehouse deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        const errorMessage = err?.data?.message || err?.message || 'Error deleting product warehouse. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol'
    }).format(amount);
  };

  // Render availability status
  const renderAvailabilityStatus = (quantity: number) => {
    if (quantity === 0) {
      return (
        <Chip
          label="Out of Stock"
          size="small"
          icon={<Warning />}
          sx={{
            bgcolor: '#fdeded',
            color: '#d72c0d',
            fontWeight: 600,
            fontSize: '12px',
            borderRadius: '6px'
          }}
        />
      );
    } else if (quantity < 10) {
      return (
        <Chip
          label={`Low Stock (${quantity})`}
          size="small"
          icon={<Warning />}
          sx={{
            bgcolor: '#ffe8d4',
            color: '#b24000',
            fontWeight: 600,
            fontSize: '12px',
            borderRadius: '6px'
          }}
        />
      );
    } else {
      return (
        <Chip
          label={`In Stock (${quantity})`}
          size="small"
          icon={<CheckCircle />}
          sx={{
            bgcolor: '#d1f4e0',
            color: '#00783e',
            fontWeight: 600,
            fontSize: '12px',
            borderRadius: '6px'
          }}
        />
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {/* Main Content Area */}
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header Section */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Product Warehouse Management
                  </Typography>
                  {isVendor && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Store fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                      <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, fontWeight: 500 }}>
                        Vendor: {userName} (ID: {vendorId})
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                      borderRadius: '6px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: shopifyColors.textSecondary,
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNew}
                    size="small"
                    sx={{
                      bgcolor: shopifyColors.primary,
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '6px',
                      px: 2,
                      '&:hover': { bgcolor: shopifyColors.primaryHover }
                    }}
                  >
                    Add Product Warehouse
                  </Button>
                </Box>
              </Box>

              

              {isLoading && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                  bgcolor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${shopifyColors.border}`
                }}>
                  <CircularProgress sx={{ color: shopifyColors.primary }} />
                  <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>
                    Loading product warehouse...
                  </Typography>
                </Box>
              )}

           
              {isError && !isLoading && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: '8px',
                    border: `1px solid ${shopifyColors.border}`
                  }}
                  action={
                    <IconButton color="inherit" size="small" onClick={handleRefresh}>
                      <Refresh />
                    </IconButton>
                  }
                >
                  Failed to load product warehouses
                </Alert>
              )}

              {/* Empty State */}
              {!isLoading && !isError && productWarehouses.length === 0 && (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${shopifyColors.border}`
                }}>
                  <Typography variant="h6" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, mb: 1 }}>
                    {searchStr || selectedProductFilter || selectedWarehouseFilter ? 'No matching records found' : 'No product warehouses available'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                    {searchStr || selectedProductFilter || selectedWarehouseFilter
                      ? 'Try adjusting your search or filters'
                      : 'Get started by adding your first product warehouse mapping'
                    }
                  </Typography>
                  {!searchStr && !selectedProductFilter && !selectedWarehouseFilter && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAddNew}
                      sx={{
                        bgcolor: shopifyColors.primary,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '6px',
                        '&:hover': { bgcolor: shopifyColors.primaryHover }
                      }}
                    >
                      Add First Product Warehouse
                    </Button>
                  )}
                </Box>
              )}

              {/* Table */}
              {!isLoading && productWarehouses.length > 0 && (
                <Card sx={{
                  borderRadius: '8px',
                  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${shopifyColors.border}`,
                  overflow: 'hidden'
                }}>
                  {/* Table Header */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '60px 80px 1fr 200px 150px 150px',
                    gap: 2,
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${shopifyColors.border}`,
                    bgcolor: '#fafbfb'
                  }}>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      #
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      Image
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      Product
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      Warehouse 
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                      Availability
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                      Actions
                    </Typography>
                  </Box>

                  {/* Table Rows */}
                  <Box sx={{ bgcolor: 'white' }}>
                    {productWarehouses.map((item: ProductWarehouse, index: number) => {
                      const enhancedItem = getProductWithImage(item);
                      return (
                        <Box
                          key={enhancedItem.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '60px 80px 1fr 200px 150px 150px',
                            gap: 2,
                            px: 3,
                            py: 2.5,
                            alignItems: 'center',
                            borderBottom: index < productWarehouses.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          {/* Row Number */}
                          <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, fontSize: '13px' }}>
                            {(page - 1) * itemsPerPage + index + 1}
                          </Typography>

                          {/* Image */}
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              src={getProductImageUrl(enhancedItem)}
                              variant="rounded"
                              sx={{
                                width: 48,
                                height: 48,
                                bgcolor: '#f1f2f4',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 600,
                                border: `1px solid ${shopifyColors.border}`
                              }}
                              imgProps={{
                                onError: handleImageError,
                                style: {
                                  objectFit: 'cover'
                                }
                              }}
                            >
                              {getProductInitial(enhancedItem)}
                            </Avatar>
                          </Box>

                          {/* Product Info */}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: shopifyColors.textPrimary, fontSize: '14px' }}>
                              {enhancedItem.product_name || 'Unnamed Product'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontSize: '12px' }}>
                              {enhancedItem.product_short_name || 'No description'}
                            </Typography>
                            {enhancedItem.product_price && (
                              <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, fontSize: '11px', display: 'block' }}>
                                Price: {formatCurrency(enhancedItem.product_price, enhancedItem.currency)}
                              </Typography>
                            )}
                          </Box>

                          {/* Warehouse Info */}
                          <Box>
                            <Chip
                              label={enhancedItem.warehouse_name || `Warehouse ${enhancedItem.warehouse_id}`}
                              size="small"
                              icon={<Store />}
                              sx={{
                                bgcolor: '#e6f6fc',
                                color: '#006fbb',
                                fontWeight: 500,
                                fontSize: '11px',
                                borderRadius: '6px'
                              }}
                            />
                          </Box>

                          {/* Availability status */}
                          <Box>
                            {renderAvailabilityStatus(enhancedItem.available_quantity)}
                          </Box>

                          {/* Actions */}
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(enhancedItem)}
                                sx={{
                                  color: shopifyColors.textSecondary,
                                  '&:hover': {
                                    bgcolor: '#f1f2f4',
                                    color: shopifyColors.textPrimary
                                  }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(enhancedItem.id)}
                                disabled={isDeleting}
                                sx={{
                                  color: shopifyColors.textSecondary,
                                  '&:hover': {
                                    bgcolor: '#fdeded',
                                    color: shopifyColors.error
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              )}

              {/* Pagination */}
              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '13px',
                      },
                      '& .Mui-selected': {
                        bgcolor: shopifyColors.primary + ' !important',
                        color: 'white',
                      },
                      '& .MuiPaginationItem-root:hover': {
                        bgcolor: '#f1f2f4',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: `1px solid ${shopifyColors.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 />
              <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                {isEditing ? 'Edit Product Warehouse' : 'Add Product Warehouse'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleFormClose}
              size="small"
              disabled={isFormSubmitting}
              sx={{
                color: shopifyColors.textSecondary,
                '&:hover': {
                  bgcolor: '#f1f2f4',
                  color: shopifyColors.textPrimary
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Product Selection - FIXED WITH SEPARATE HANDLER */}
              <FormControl fullWidth error={!!formErrors.product_id} disabled={isFormSubmitting || isLoadingProducts}>
                <InputLabel id="product-label">Product *</InputLabel>
                <Select
                  labelId="product-label"
                  id="product-select"
                  value={formData.product_id}
                  onChange={handleProductChange}
                  label="Product *"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  <MenuItem value={0} disabled>
                    <em>Select Product</em>
                  </MenuItem>
                  {isLoadingProducts ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <em>Loading products...</em>
                      </Box>
                    </MenuItem>
                  ) : products && products.length > 0 ? (
                    products.map((product) => (
                      <MenuItem key={product.product_id || product.id} value={product.product_id || product.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Avatar
                            src={product.product_main_image ? getProductImageUrl({
                              product_main_image: product.product_main_image
                            } as ProductWarehouse) : undefined}
                            variant="rounded"
                            sx={{ width: 32, height: 32 }}
                            imgProps={{
                              onError: handleImageError
                            }}
                          >
                            {product.prod_short_name?.[0] || product.prod_name?.[0] || 'P'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {product.prod_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              {product.prod_short_name} • {formatCurrency(product.product_price || 0, product.currency)}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No products available</em>
                    </MenuItem>
                  )}
                </Select>
                {formErrors.product_id && (
                  <FormHelperText error>{formErrors.product_id}</FormHelperText>
                )}
                {!isLoadingProducts && products.length === 0 && (
                  <FormHelperText>
                    No products found. Please add a product first.
                  </FormHelperText>
                )}
              </FormControl>

              {/* Warehouse Selection */}
              <FormControl fullWidth error={!!formErrors.warehouse_id} disabled={isFormSubmitting || isLoadingWarehouses}>
                <InputLabel id="warehouse-label">Warehouse *</InputLabel>
                <Select
                  labelId="warehouse-label"
                  id="warehouse-select"
                  value={formData.warehouse_id}
                  onChange={handleWarehouseChange}
                  label="Warehouse *"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  <MenuItem value={0} disabled>
                    <em>Select Warehouse</em>
                  </MenuItem>
                  {isLoadingWarehouses ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <em>Loading warehouses...</em>
                      </Box>
                    </MenuItem>
                  ) : warehouses.length > 0 ? (
                    warehouses
                      .filter((warehouse: Warehouse) => warehouse.status === 1)
                      .map((warehouse: Warehouse) => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              <Store fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {warehouse.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              ID: {warehouse.id} • Status: Active
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>
                      <em>No warehouses available</em>
                    </MenuItem>
                  )}
                </Select>
                {formErrors.warehouse_id && (
                  <FormHelperText error>{formErrors.warehouse_id}</FormHelperText>
                )}
                {!isLoadingWarehouses && (
                  <FormHelperText>
                    {warehouses.length === 0 
                      ? 'No warehouses found. Please add a warehouse in Warehouse Management.'
                      : warehouses.filter((w: Warehouse) => w.status === 1).length === 0
                      ? 'All warehouses are inactive. Please activate at least one warehouse.'
                      : `${warehouses.filter((w: Warehouse) => w.status === 1).length} active warehouse(s) available`
                    }
                  </FormHelperText>
                )}
              </FormControl>

              {/* Available Quantity */}
              <TextField
                name="available_quantity"
                label="Available Quantity *"
                type="number"
                value={formData.available_quantity}
                onChange={handleInputChange}
                error={!!formErrors.available_quantity}
                helperText={formErrors.available_quantity || 'Enter the quantity available in this warehouse'}
                fullWidth
                required
                inputProps={{ min: 0, step: 1 }}
                variant="outlined"
                disabled={isFormSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Inventory />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  }
                }}
              />

              {/* Quantity Status Preview */}
              {formData.available_quantity >= 0 && (
                <Box sx={{
                  p: 2,
                  bgcolor: formData.available_quantity === 0 ? '#fdeded' : formData.available_quantity < 10 ? '#ffe8d4' : '#f0f9f0',
                  borderRadius: '6px',
                  border: `1px solid ${formData.available_quantity === 0 ? '#d72c0d' : formData.available_quantity < 10 ? '#b24000' : '#d1f4e0'}`
                }}>
                  <Typography variant="caption" sx={{ 
                    color: formData.available_quantity === 0 ? '#d72c0d' : formData.available_quantity < 10 ? '#b24000' : '#00783e', 
                    fontWeight: 600, 
                    fontSize: '11px', 
                    textTransform: 'uppercase' 
                  }}>
                    Status Preview
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {renderAvailabilityStatus(formData.available_quantity)}
                  </Box>
                  <Typography variant="caption" sx={{ 
                    color: shopifyColors.textSecondary,
                    display: 'block',
                    mt: 1
                  }}>
                    {formData.available_quantity === 0 && 'This product will show as out of stock'}
                    {formData.available_quantity > 0 && formData.available_quantity < 10 && 'This product will show as low stock'}
                    {formData.available_quantity >= 10 && 'This product will show as in stock'}
                  </Typography>
                </Box>
              )}

              {/* Info Box */}
              <Box sx={{
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: '6px',
                border: `1px solid ${shopifyColors.border}`
              }}>
                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                  <strong>Note:</strong> This mapping controls how much of this product is available in the selected warehouse. 
                  {isEditing ? ' Updating this will modify the existing warehouse allocation.' : ' Adding this will create a new warehouse allocation for the product.'}
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1, gap: 1, borderTop: `1px solid ${shopifyColors.border}` }}>
            <Button
              onClick={handleFormClose}
              disabled={isFormSubmitting}
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: shopifyColors.textPrimary,
                borderColor: shopifyColors.border,
                borderRadius: '6px',
                px: 2.5,
                '&:hover': {
                  borderColor: shopifyColors.textSecondary,
                  bgcolor: 'transparent',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormSubmitting || !formData.product_id || !formData.warehouse_id || formData.available_quantity < 0}
              sx={{
                bgcolor: (!formData.product_id || !formData.warehouse_id || formData.available_quantity < 0) ? '#919eab' : shopifyColors.primary,
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '6px',
                px: 2.5,
                '&:hover': { 
                  bgcolor: (!formData.product_id || !formData.warehouse_id || formData.available_quantity < 0) ? '#919eab' : shopifyColors.primaryHover 
                }
              }}
            >
              {isFormSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {isEditing ? 'Updating...' : 'Adding...'}
                </Box>
              ) : (
                isEditing ? 'Update Product Warehouse' : 'Add Product Warehouse'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '6px',
            fontWeight: 500
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductWarehouseManagement;

{/* */}

